import { useQueryClient } from "react-query";
import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import {
  IAspsp,
  IBankAccount,
  IBankConnection,
  IFinalizeAuthRequest,
  IFinancialOperation,
  ISettlement,
  ISettlementRequest,
  IStartAuthRequest,
  IStartAuthResponse,
} from "dto/Banking/IBanking";

/**
 * Integracja bankowa (Open Banking / PSD2) — usługa AIS przez Enable Banking.
 *
 * Wszystkie wywołania trafiają do naszego BE (kontroler /banking/*), który jest
 * proxy do Enable Banking. Certyfikaty, klucze JWT, sekrety aplikacji i tokeny
 * sesji NIE opuszczają backendu — FE operuje wyłącznie na własnym tokenie OIDC.
 * Kontrakt BE: docs/backend-banking.md.
 */

// ---------------------------------------------------------------------------
// Wspólne stałe przepływu autoryzacji (współdzielone przez rejestr Operacje
// finansowe i kreator integracji w Ustawieniach — jedna ścieżka integracji).
// ---------------------------------------------------------------------------

/** Klucz sessionStorage do weryfikacji powrotu (anty-CSRF) z banku. */
export const BANK_AUTH_STATE_KEY = "banking.auth.state";

/**
 * Wspólny adres powrotu po SCA. Callback (code/state) jest obsługiwany w rejestrze
 * „Operacje finansowe" — niezależnie od tego, skąd uruchomiono podłączenie banku.
 */
export const getBankingRedirectUrl = (): string =>
  `${window.location.origin}/registry/operacje-finansowe`;

// ---------------------------------------------------------------------------
// Funkcje API
// ---------------------------------------------------------------------------

/** Lista banków możliwych do podłączenia (opcjonalnie po kraju). */
const getAspspsApi = async (country = "PL"): Promise<IAspsp[]> =>
  (
    await axiosInstance.get<IAspsp[]>("/banking/aspsps", {
      params: { Country: country || undefined },
    })
  ).data;

/** Rozpoczęcie autoryzacji dostępu do banku — zwraca URL do przekierowania (SCA). */
const startAuthApi = async (
  request: IStartAuthRequest,
): Promise<IStartAuthResponse> =>
  (await axiosInstance.post<IStartAuthResponse>("/banking/auth", request)).data;

/** Finalizacja zgody po powrocie z banku (callback z parametrami code/state). */
const finalizeAuthApi = async (
  request: IFinalizeAuthRequest,
): Promise<IBankConnection> =>
  (await axiosInstance.post<IBankConnection>("/banking/auth/finalize", request))
    .data;

/** Lista aktywnych połączeń (zgód) z bankami w danym workspace. */
const getConnectionsApi = async (): Promise<IBankConnection[]> =>
  (await axiosInstance.get<IBankConnection[]>("/banking/connections")).data;

/** Odłączenie banku / odwołanie zgody. */
const deleteConnectionApi = async (connectionId: string): Promise<void> => {
  await axiosInstance.delete(`/banking/connections/${connectionId}`);
};

/** Odświeżenie: dociąga nowe operacje z banku i aktualizuje salda. Zwraca liczbę nowych. */
const refreshApi = async (): Promise<number> =>
  (await axiosInstance.post<number>("/banking/refresh")).data;

/** Odnotowanie operacji na koncie rozrachunkowym. */
const saveSettlementApi = async (
  operationId: string,
  request: ISettlementRequest,
): Promise<ISettlement> =>
  (
    await axiosInstance.post<ISettlement>(
      `/banking/operations/${operationId}/settlement`,
      request,
    )
  ).data;

/** Zapis notatki do operacji. */
const saveNoteApi = async (operationId: string, note: string): Promise<void> => {
  await axiosInstance.post(`/banking/operations/${operationId}/note`, { Note: note });
};

/** Podłączone rachunki (po wszystkich połączeniach). */
const getAccountsApi = async (): Promise<IBankAccount[]> =>
  (await axiosInstance.get<IBankAccount[]>("/banking/accounts")).data;

/** Operacje finansowe (transakcje), opcjonalnie filtrowane tekstem i rachunkiem. */
const getOperationsApi = async (
  searchText?: string,
  accountId?: string,
  includeArchived?: boolean,
): Promise<IFinancialOperation[]> =>
  (
    await axiosInstance.get<IFinancialOperation[]>("/banking/operations", {
      params: {
        Text: searchText?.trim() || undefined,
        AccountId: accountId || undefined,
        IncludeArchived: includeArchived || undefined,
      },
    })
  ).data;

// ---------------------------------------------------------------------------
// Klucze zapytań
// ---------------------------------------------------------------------------

const QUERY_KEYS = {
  aspsps: "banking.aspsps",
  connections: "banking.connections",
  accounts: "banking.accounts",
  operations: "banking.operations",
};

// ---------------------------------------------------------------------------
// Hooki React Query
// ---------------------------------------------------------------------------

const bankingService = {
  queryKeys: QUERY_KEYS,

  useGetAspsps: (country = "PL", enabled = true) =>
    useCustomQuery<IAspsp[]>(
      [QUERY_KEYS.aspsps, country],
      () => getAspspsApi(country),
      { enabled, retry: false },
    ),

  useGetConnections: () =>
    useCustomQuery<IBankConnection[]>(
      [QUERY_KEYS.connections],
      () => getConnectionsApi(),
      { retry: false },
    ),

  useGetAccounts: () =>
    useCustomQuery<IBankAccount[]>(
      [QUERY_KEYS.accounts],
      () => getAccountsApi(),
      { retry: false },
    ),

  useGetOperations: (searchText?: string, accountId?: string, includeArchived?: boolean) =>
    useCustomQuery<IFinancialOperation[]>(
      [QUERY_KEYS.operations, searchText ?? "", accountId ?? "", includeArchived ? "archived" : ""],
      () => getOperationsApi(searchText, accountId, includeArchived),
      { retry: false },
    ),

  useStartAuth: () =>
    useCustomMutation<IStartAuthResponse, IStartAuthRequest>((request) =>
      startAuthApi(request),
    ),

  useFinalizeAuth: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<IBankConnection, IFinalizeAuthRequest>(
      (request) => finalizeAuthApi(request),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEYS.connections]);
          queryClient.invalidateQueries([QUERY_KEYS.accounts]);
          queryClient.invalidateQueries([QUERY_KEYS.operations]);
        },
      },
    );
  },

  useDeleteConnection: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<void, string>(
      (connectionId) => deleteConnectionApi(connectionId),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEYS.connections]);
          queryClient.invalidateQueries([QUERY_KEYS.accounts]);
          queryClient.invalidateQueries([QUERY_KEYS.operations]);
        },
      },
    );
  },

  useRefresh: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<number, void>(() => refreshApi(), {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.connections]);
        queryClient.invalidateQueries([QUERY_KEYS.accounts]);
        queryClient.invalidateQueries([QUERY_KEYS.operations]);
      },
    });
  },

  useSaveSettlement: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<
      ISettlement,
      { operationId: string; request: ISettlementRequest }
    >(({ operationId, request }) => saveSettlementApi(operationId, request), {
      onSuccess: () => {
        queryClient.invalidateQueries([QUERY_KEYS.operations]);
      },
    });
  },

  useSaveNote: () => {
    const queryClient = useQueryClient();
    return useCustomMutation<void, { operationId: string; note: string }>(
      ({ operationId, note }) => saveNoteApi(operationId, note),
      {
        onSuccess: () => {
          queryClient.invalidateQueries([QUERY_KEYS.operations]);
        },
      },
    );
  },
};

export default bankingService;
